import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Block as BlockIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import service, {
  Convite,
  ReferralConfig,
  ReferralStats,
  Referrer,
} from '../../services/marketing-referral.service';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';
import {
  Handshake,
  GroupAddOutlined,
  MailOutlined,
  CheckCircleOutlined,
  StarsOutlined,
} from '@mui/icons-material';

const formatDate = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—');

export default function Referral() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [convites, setConvites] = useState<Convite[]>([]);
  const [searchRef, setSearchRef] = useState('');
  const [searchConv, setSearchConv] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [cfg, setCfg] = useState<ReferralConfig | null>(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      setStats(await service.stats());
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  }, []);

  const loadReferrers = useCallback(async () => {
    setLoading(true);
    try {
      setReferrers(await service.listReferrers(searchRef || undefined));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [searchRef]);

  const loadConvites = useCallback(async () => {
    setLoading(true);
    try {
      setConvites(await service.listConvites(statusFilter || undefined, searchConv || undefined));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchConv]);

  const loadCfg = useCallback(async () => {
    try {
      setCfg(await service.getConfig());
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === 1) loadReferrers();
    else if (tab === 2) loadConvites();
    else if (tab === 3) loadCfg();
  }, [tab, loadReferrers, loadConvites, loadCfg]);

  const invalidate = async (c: Convite) => {
    if (!window.confirm('Invalidar este convite?')) return;
    try {
      await service.invalidateConvite(c.id);
      setToast({ msg: 'Convite invalidado', severity: 'success' });
      await loadConvites();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const saveCfg = async () => {
    if (!cfg) return;
    setSavingCfg(true);
    try {
      const updated = await service.updateConfig(cfg);
      setCfg(updated);
      setToast({ msg: 'Configuração guardada', severity: 'success' });
      await loadStats();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setSavingCfg(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Programa de Referral"
        subtitle="Convida 1 amigo, ganha bónus — construir rede pelos utilizadores"
        icon={<Handshake />}
        actions={
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadStats();
              if (tab === 1) loadReferrers();
              else if (tab === 2) loadConvites();
              else if (tab === 3) loadCfg();
            }}
          >
            Atualizar
          </Button>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <StatCard icon={<GroupAddOutlined />} label="Total referrers" value={stats?.total_referrers ?? '—'} color="primary" />
        <StatCard icon={<MailOutlined />} label="Convites" value={stats?.total_convites ?? '—'} color="info" />
        <StatCard icon={<CheckCircleOutlined />} label="Aceites" value={stats?.convites_aceites ?? '—'} color="success" />
        <StatCard icon={<StarsOutlined />} label="Pontos distribuídos" value={stats?.bonus_pago_pontos?.toLocaleString('pt-PT') ?? '—'} color="warning" />
      </Box>

      {stats?.top_referrer && (
        <Alert severity="info" sx={{ mb: 2 }}>
          🏆 Top referrer: <b>{stats.top_referrer.nome}</b> ({stats.top_referrer.codigo}) —{' '}
          {stats.top_referrer.total_convites} convites, {stats.top_referrer.total_bonus} pts
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Visão geral" />
          <Tab label={`Referrers${referrers.length ? ` (${referrers.length})` : ''}`} />
          <Tab label={`Convites${convites.length ? ` (${convites.length})` : ''}`} />
          <Tab label="Configuração" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Alert severity="info">
          Selecione uma tab para gerir referrers, convites ou ajustar a configuração do programa.
        </Alert>
      )}

      {tab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar por nome, email ou código"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadReferrers()}
            />
          </Paper>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Referrer</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell align="right">Convites</TableCell>
                    <TableCell align="right">Bónus total</TableCell>
                    <TableCell>Criado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : referrers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <EmptyState
                          icon={<GroupAddOutlined />}
                          title="Sem referrers"
                          description="Nenhum utilizador gerou código ainda. Códigos são criados automaticamente quando o user acede ao programa na app."
                          compact
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    referrers.map((r) => (
                      <TableRow key={r.user_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{r.nome}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={r.codigo} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{r.total_convites}</TableCell>
                        <TableCell align="right">{Number(r.total_bonus).toLocaleString('pt-PT')} pts</TableCell>
                        <TableCell>{formatDate(r.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {tab === 2 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Pesquisar"
                value={searchConv}
                onChange={(e) => setSearchConv(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadConvites()}
              />
              <TextField
                size="small"
                label="Estado"
                select
                SelectProps={{ native: true }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <option value="">Todos</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="INVALIDATED">Invalidated</option>
              </TextField>
            </Stack>
          </Paper>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Referrer</TableCell>
                    <TableCell>Convidado</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Bónus</TableCell>
                    <TableCell>Criado</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : convites.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ border: 0 }}>
                        <EmptyState
                          icon={<MailOutlined />}
                          title="Sem convites"
                          description="Ainda ninguém usou um código. Quando aceitarem, aparecem aqui com o estado e o bónus creditado."
                          compact
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    convites.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Typography variant="body2">{c.referrer_nome}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.referrer_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.convidado_nome || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.convidado_email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={c.codigo} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={c.status}
                            color={c.status === 'COMPLETED' ? 'success' : c.status === 'INVALIDATED' ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">{Number(c.bonus_pago).toLocaleString('pt-PT')} pts</TableCell>
                        <TableCell>{formatDate(c.created_at)}</TableCell>
                        <TableCell align="right">
                          {c.status !== 'INVALIDATED' && (
                            <Tooltip title="Invalidar">
                              <IconButton size="small" color="error" onClick={() => invalidate(c)}>
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {tab === 3 && cfg && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Configuração do Programa
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Estes valores são lidos pelo endpoint cliente <code>POST /v1/client/hub/referral/aplicar</code> com cache de 5 min.
          </Alert>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Pontos ao referrer (por convite aceite)"
                value={cfg.bonus_referrer_pontos}
                onChange={(e) => setCfg({ ...cfg, bonus_referrer_pontos: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Pontos ao convidado (welcome)"
                value={cfg.bonus_convidado_pontos}
                onChange={(e) => setCfg({ ...cfg, bonus_convidado_pontos: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="CVE extra ao referrer (opcional)"
                value={cfg.bonus_referrer_cve}
                onChange={(e) => setCfg({ ...cfg, bonus_referrer_cve: Number(e.target.value) })}
                helperText="Creditado na wallet ativa do referrer (0 = só pontos)"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="CVE extra ao convidado (opcional)"
                value={cfg.bonus_convidado_cve}
                onChange={(e) => setCfg({ ...cfg, bonus_convidado_cve: Number(e.target.value) })}
                helperText="Creditado na wallet do convidado (0 = só pontos)"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Limite de utilizadores (0 = sem limite)"
                value={cfg.limite_utilizadores}
                onChange={(e) => setCfg({ ...cfg, limite_utilizadores: Number(e.target.value) })}
                helperText={
                  cfg.limite_utilizadores > 0 && stats
                    ? `Usados ${stats.convites_aceites} / ${cfg.limite_utilizadores}${
                        stats.convites_aceites >= cfg.limite_utilizadores ? ' — ESGOTADO' : ''
                      }`
                    : 'Ex: 500 para urgência inicial (Passo 4 do plano)'
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={cfg.ativo}
                    onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })}
                  />
                }
                label="Programa ativo"
              />
            </Grid>
            <Grid size={12}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveCfg} disabled={savingCfg}>
                {savingCfg ? 'A guardar…' : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
